import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ticketsService, Ticket, CreateTicketData } from '../../services/tickets'

export const useTickets = (companyId?: string) => {
  return useQuery({
    queryKey: ['tickets', companyId ?? 'all'],
    queryFn: () => ticketsService.getTickets(companyId),
    enabled: companyId !== undefined ? companyId !== '' : true
  })
}

export const useTicket = (ticketId: string) => {
  return useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => ticketsService.getTicket(ticketId),
    enabled: !!ticketId
  })
}

export const useCreateTicket = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ticketData, companyId, createdByUserId }: { 
      ticketData: CreateTicketData; 
      companyId: string; 
      createdByUserId: string 
    }) => ticketsService.createTicket(ticketData, companyId, createdByUserId),
    onSuccess: (_, { companyId }) => {
      // Invalidate and refetch tickets list
      queryClient.invalidateQueries({ queryKey: ['tickets', companyId] })
      queryClient.invalidateQueries({ queryKey: ['tickets', 'all'] })
    }
  })
}

export const useUpdateTicket = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ticketId, updates }: { ticketId: string; updates: Partial<Ticket> }) =>
      ticketsService.updateTicket(ticketId, updates),
    onSuccess: (updatedTicket) => {
      // Update the specific ticket in cache
      queryClient.setQueryData(['ticket', updatedTicket.id], updatedTicket)
      // Invalidate tickets list to refetch
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['tickets', 'all'] })
    }
  })
}

export const useDeleteTicket = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ticketId: string) => ticketsService.deleteTicket(ticketId),
    onSuccess: (_, ticketId) => {
      // Remove ticket from cache
      queryClient.removeQueries({ queryKey: ['ticket', ticketId] })
      // Invalidate tickets list to refetch
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['tickets', 'all'] })
    }
  })
}
